import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styles from "./JoinInviteModal.module.css";
import { resolveJoinInvite } from "../../data/joinInviteConfig";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import {
  isJoinInviteHidden,
  rememberJoinInviteDismissed,
} from "../../data/joinInviteStorage";
import {
  isValidEmailAddress,
  KIT_SUPPORT_EMAIL,
  KIT_SUBSCRIBE_URL,
  subscribeToKit,
} from "../../data/kitSubscribe";
import { linkifyEmails } from "../../data/linkifyEmails";
import { lockBodyScroll, unlockBodyScroll } from "../../Hooks/bodyScrollLock";
import logo from "../../assets/media/logo.png";

const readForceParam = (search) => {
  const params = new URLSearchParams(search || "");
  if (!params.has("joininvite")) return null;
  const v = String(params.get("joininvite") || "")
    .trim()
    .toLowerCase();
  if (v === "0" || v === "false" || v === "off") return "off";
  return "on";
};

const isConnectedPath = (location) => {
  const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/connected") return true;
  const params = new URLSearchParams(location.search || "");
  return params.has("connected") || params.has("confirmed");
};

const isCoarsePointer = () =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);

/** Keep a position:fixed overlay inside iOS Safari’s visual viewport while the keyboard is open. */
const pinToVisualViewport = (el) => {
  if (!el) return;
  const vv = window.visualViewport;
  if (!vv) {
    el.style.top = "";
    el.style.left = "";
    el.style.right = "";
    el.style.bottom = "";
    el.style.height = "";
    return;
  }
  el.style.top = `${Math.max(0, vv.offsetTop)}px`;
  el.style.left = "0px";
  el.style.right = "0px";
  el.style.bottom = "auto";
  el.style.height = `${Math.max(0, vv.height)}px`;
};

/** Recover if Safari still auto-zoomed on focus (font-size < 16px). */
const resetFocusZoom = () => {
  const vv = window.visualViewport;
  if (!vv || vv.scale <= 1.01) return;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const prev =
    meta.getAttribute("content") || "width=device-width, initial-scale=1";
  meta.setAttribute(
    "content",
    "width=device-width, initial-scale=1, maximum-scale=1",
  );
  window.setTimeout(() => {
    meta.setAttribute("content", prev);
  }, 50);
};

const JoinInviteModal = ({ disabled = false }) => {
  const location = useLocation();
  const { content, catalog, isPreview } = usePreviewStores();
  const loadingStatus = useSelector(
    (state) => state.loadingRequests.pendingLoadRequests,
  );
  const copy = useMemo(
    () => resolveJoinInvite(content, catalog),
    [content, catalog],
  );

  const force = readForceParam(location.search);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [succeeded, setSucceeded] = useState(false);
  const emailRef = useRef(null);
  const closeRef = useRef(null);
  const overlayRef = useRef(null);
  const openedRef = useRef(false);

  const skipConnected = isConnectedPath(location);
  const canOffer =
    force !== "off" &&
    !disabled &&
    !skipConnected &&
    (copy.enabled || force === "on");

  useEffect(() => {
    if (!canOffer) setOpen(false);
  }, [canOffer]);

  const dismiss = useCallback(
    (opts = {}) => {
      const persist = opts.persist !== false;
      if (persist && force !== "on" && !isPreview) {
        rememberJoinInviteDismissed(copy.hideDays);
      }
      setOpen(false);
      openedRef.current = true;
    },
    [copy.hideDays, force, isPreview],
  );

  useEffect(() => {
    if (!canOffer) return undefined;

    if (force === "on") {
      setOpen(true);
      openedRef.current = true;
      return undefined;
    }

    if (openedRef.current) return undefined;
    if (!isPreview && isJoinInviteHidden()) return undefined;

    let shown = false;
    let delayId = 0;
    let fallbackId = 0;
    const show = () => {
      if (shown || openedRef.current) return;
      if (!isPreview && isJoinInviteHidden()) return;
      shown = true;
      openedRef.current = true;
      setOpen(true);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      const pct = (window.scrollY / max) * 100;
      if (pct >= copy.scrollPercent) show();
    };

    const startTriggers = () => {
      const delayMs = Math.max(0, copy.delaySeconds) * 1000;
      delayId = window.setTimeout(show, delayMs);
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    };

    if (loadingStatus === 0) {
      startTriggers();
    } else {
      fallbackId = window.setTimeout(startTriggers, 10000);
    }

    return () => {
      window.clearTimeout(delayId);
      window.clearTimeout(fallbackId);
      window.removeEventListener("scroll", onScroll);
    };
  }, [canOffer, force, copy.delaySeconds, copy.scrollPercent, loadingStatus, isPreview]);

  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    const overlay = overlayRef.current;
    pinToVisualViewport(overlay);
    const vv = window.visualViewport;
    const onViewportChange = () => pinToVisualViewport(overlay);
    vv?.addEventListener("resize", onViewportChange);
    vv?.addEventListener("scroll", onViewportChange);
    window.addEventListener("orientationchange", onViewportChange);

    const focusId = window.requestAnimationFrame(() => {
      // Programmatic focus on iPhone opens the keyboard and can zoom/offset
      // the page before the overlay has pinned to the visual viewport.
      if (!isCoarsePointer()) emailRef.current?.focus();
    });
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusId);
      document.removeEventListener("keydown", onKeyDown);
      vv?.removeEventListener("resize", onViewportChange);
      vv?.removeEventListener("scroll", onViewportChange);
      window.removeEventListener("orientationchange", onViewportChange);
      if (overlay) {
        overlay.style.top = "";
        overlay.style.left = "";
        overlay.style.right = "";
        overlay.style.bottom = "";
        overlay.style.height = "";
      }
      unlockBodyScroll();
    };
  }, [open, dismiss]);

  useEffect(() => {
    if (!open || !succeeded) return undefined;
    const id = window.setTimeout(() => dismiss({ persist: false }), 2800);
    return () => window.clearTimeout(id);
  }, [open, succeeded, dismiss]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || succeeded) return;
    const clean = String(email).replaceAll(" ", "");
    if (!isValidEmailAddress(clean)) {
      setError(
        `"${clean}" does not appear to be a valid email address. Please fix it and try again.`,
      );
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await subscribeToKit(clean);
      rememberJoinInviteDismissed(copy.hideDays);
      setSucceeded(true);
      setEmail("");
    } catch (err) {
      setError(
        err?.message ||
          `We could not add that email. Please try again, or write ${KIT_SUPPORT_EMAIL}.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const coverUrl = copy.showCover ? copy.coverImageUrl : "";

  return createPortal(
    <div
      ref={overlayRef}
      className={styles.modal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-invite-title"
      aria-describedby="join-invite-lead"
      data-join-invite="true"
      data-join-invite-success={succeeded ? "true" : "false"}
      data-scroll-lock-scrollable
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div
        className={styles.frame}
        data-has-cover={copy.showCover ? "true" : "false"}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {copy.showCover &&
          (coverUrl ? (
            <div className={styles.cover} aria-hidden="true">
              <img src={coverUrl} alt="" />
            </div>
          ) : (
            <div
              className={styles.cover}
              data-fallback="true"
              aria-hidden="true"
            >
              <div className={styles["cover-wash"]} />
            </div>
          ))}
        <div className={styles.panel} data-scroll-lock-scrollable>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            onClick={() => dismiss()}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
          {copy.showLogo && (
            <img
              src={logo}
              alt="Ignite Elysium"
              className={styles.logo}
              width="160"
              height="40"
            />
          )}
          <h2 id="join-invite-title" className={styles.headline}>
            {copy.headline}
          </h2>
          <p id="join-invite-lead" className={styles.lead}>
            {copy.lead}
          </p>
          {succeeded ? (
            <p className={styles.success} role="status">
              {copy.success}
            </p>
          ) : (
            <form
              className={styles.form}
              action={KIT_SUBSCRIBE_URL}
              method="post"
              onSubmit={onSubmit}
              noValidate
              aria-busy={isSubmitting ? "true" : undefined}
            >
              <label className={styles["visually-hidden"]} htmlFor="join-invite-email">
                Email address
              </label>
              <input
                ref={emailRef}
                id="join-invite-email"
                type="email"
                name="email_address"
                autoComplete="email"
                inputMode="email"
                className={styles.email}
                placeholder={copy.emailPlaceholder}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                onBlur={() => {
                  resetFocusZoom();
                  pinToVisualViewport(overlayRef.current);
                }}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className={styles.submit}
                disabled={isSubmitting}
              >
                {copy.button}
              </button>
            </form>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          {copy.showLegal && !succeeded && (
            <p className={styles.legal}>{linkifyEmails(copy.legal)}</p>
          )}
          {copy.showDismissLink && !succeeded && (
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => dismiss()}
            >
              {copy.dismissLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default JoinInviteModal;
